// src/seo/Seo.jsx

import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_URL = "https://www.onepickgame.com";

const SUPPORTED_LANGS = [
  "en",
  "ko",
  "ja",
  "zh",
  "ru",
  "pt",
  "es",
  "fr",
  "id",
  "hi",
  "de",
  "vi",
  "ar",
  "bn",
  "th",
  "tr",
 "it", "pl", "nl", "cs"];

const OG_LOCALE_MAP = {
  en: "en_US",
  ko: "ko_KR",
  ja: "ja_JP",
  zh: "zh_CN",
  ru: "ru_RU",
  pt: "pt_BR",
  es: "es_ES",
  fr: "fr_FR",
  id: "id_ID",
  hi: "hi_IN",
  de: "de_DE",
  vi: "vi_VN",
  ar: "ar_AR",
  bn: "bn_IN",
  th: "th_TH",
  tr: "tr_TR",

"it":"it_IT",
"pl":"pl_PL",
"nl":"nl_NL",
"cs":"cs_CZ"
};

function stripTrailingSlash(value = "") {
  return String(value).replace(/\/+$/, "");
}

function stripSlashes(value = "") {
  return String(value).replace(/^\/+|\/+$/g, "");
}

function normalizeCanonical(
  base,
  lang,
  slug = "",
  { langPrefix = true } = {}
) {
  const normalizedBase = stripTrailingSlash(base || SITE_URL);
  const normalizedLang = String(lang || "en").split("-")[0];
  const normalizedSlug = stripSlashes(slug);

  if (!langPrefix) {
    return normalizedSlug
      ? `${normalizedBase}/${normalizedSlug}`
      : normalizedBase;
  }

  return normalizedSlug
    ? `${normalizedBase}/${normalizedLang}/${normalizedSlug}`
    : `${normalizedBase}/${normalizedLang}`;
}

function toAbsoluteUrl(origin, url) {
  if (!url) return "";

  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  const normalizedOrigin = stripTrailingSlash(origin || SITE_URL);

  const normalizedUrl = String(url).startsWith("/")
    ? url
    : `/${url}`;

  return `${normalizedOrigin}${normalizedUrl}`;
}

export default function Seo({
  lang = "en",
  slug = "",
  title = "OnePickGame",
  description = "Create and play tournament bracket games on OnePickGame.",
  image = "/ogimg.png",
  indexable = true,
  langPrefix = true,
  hreflangLangs,
  type = "website",
}) {
  const normalizedLang = String(lang || "en").split("-")[0];

  const safeTitle = String(title || "OnePickGame")
    .replace(/\s+/g, " ")
    .trim();

  const safeDescription = String(
    description ||
      "Create and play tournament bracket games on OnePickGame."
  )
    .replace(/\s+/g, " ")
    .trim();

  const origin = SITE_URL;

  const canonical = normalizeCanonical(
    origin,
    normalizedLang,
    slug,
    {
      langPrefix,
    }
  );

  const absoluteImage = image
    ? toAbsoluteUrl(origin, image)
    : "";

  const langsForHreflang = [...new Set(hreflangLangs ?? SUPPORTED_LANGS)]
    .filter((language) => SUPPORTED_LANGS.includes(language));

  const hreflangs = indexable
    ? langsForHreflang.map((language) => ({
        hreflang: language,
        href: normalizeCanonical(
          origin,
          language,
          slug,
          {
            langPrefix,
          }
        ),
      }))
    : [];

  const ogLocale =
    OG_LOCALE_MAP[normalizedLang] || "en_US";

  const alternateOgLocales = langsForHreflang
    .filter((language) => language !== normalizedLang)
    .map((language) => OG_LOCALE_MAP[language])
    .filter(Boolean);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: safeTitle,
    description: safeDescription,
    url: canonical,
    inLanguage: normalizedLang,
    isPartOf: {
      "@type": "WebSite",
      name: "OnePickGame",
      url: SITE_URL,
    },
    ...(absoluteImage
      ? {
          primaryImageOfPage: {
            "@type": "ImageObject",
            url: absoluteImage,
          },
        }
      : {}),
  };

  return (
    <Helmet>
      {/* 기본 SEO */}
      <html lang={normalizedLang} dir={normalizedLang === "ar" ? "rtl" : "ltr"} />

      <title>{safeTitle}</title>

      <meta
        name="description"
        content={safeDescription}
      />

      <meta
        name="robots"
        content={
          indexable
            ? "index, follow, max-image-preview:large"
            : "noindex, follow"
        }
      />

      {/* Canonical */}
      <link
        rel="canonical"
        href={canonical}
      />

      {/* hreflang */}
      {hreflangs.map(({ hreflang, href }) => (
        <link
          key={hreflang}
          rel="alternate"
          hrefLang={hreflang}
          href={href}
        />
      ))}

      {indexable && langsForHreflang.includes("en") && (
        <link
          rel="alternate"
          hrefLang="x-default"
          href={normalizeCanonical(
            origin,
            "en",
            slug,
            {
              langPrefix,
            }
          )}
        />
      )}

      {/* Open Graph */}
      <meta
        property="og:type"
        content={type}
      />

      <meta
        property="og:title"
        content={safeTitle}
      />

      <meta
        property="og:description"
        content={safeDescription}
      />

      <meta
        property="og:url"
        content={canonical}
      />

      <meta
        property="og:site_name"
        content="OnePickGame"
      />

      <meta
        property="og:locale"
        content={ogLocale}
      />

      {alternateOgLocales.map((locale) => (
        <meta
          key={locale}
          property="og:locale:alternate"
          content={locale}
        />
      ))}

      {absoluteImage && (
          <meta
            property="og:image"
            content={absoluteImage}
          />
      )}
      {absoluteImage && (
          <meta
            property="og:image:alt"
            content={safeTitle}
          />
      )}

      {/* Twitter */}
      <meta
        name="twitter:card"
        content="summary_large_image"
      />

      <meta
        name="twitter:title"
        content={safeTitle}
      />

      <meta
        name="twitter:description"
        content={safeDescription}
      />

      {absoluteImage && (
          <meta
            name="twitter:image"
            content={absoluteImage}
          />
      )}
      {absoluteImage && (
          <meta
            name="twitter:image:alt"
            content={safeTitle}
          />
      )}

      {/* JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(jsonLd).replace(/</g, "\\u003c")}
      </script>
    </Helmet>
  );
}
